import React from 'react';
import type { InventoryItem } from '../../../types';
import XCircleIcon from '../../icons/XCircleIcon';

interface InventoryUpdateDialogProps {
  itemName: string;
  currentStock: number;
  receivedQuantity: number;
  unit: string;
  onAddStock: () => void;
  onAdjustQuantity: () => void;
  onSkip: () => void;
  onClose: () => void;
}

const InventoryUpdateDialog: React.FC<InventoryUpdateDialogProps> = ({
  itemName,
  currentStock,
  receivedQuantity,
  unit,
  onAddStock,
  onAdjustQuantity,
  onSkip,
  onClose,
}) => {
  const newTotal = currentStock + receivedQuantity;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-bold text-gray-800">Update Inventory</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <XCircleIcon className="w-6 h-6" />
          </button>
        </div>

        <div className="mb-6">
          <p className="text-gray-700 mb-4">
            <strong className="text-lg">{itemName}</strong> already exists in inventory.
          </p>
          
          <div className="bg-gray-50 p-4 rounded-lg space-y-2 mb-4">
            <div className="flex justify-between">
              <span className="text-gray-600">Current Inventory Stock:</span>
              <span className="font-semibold">{currentStock} {unit}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Physical Stock Received:</span>
              <span className="font-semibold text-blue-600">+{receivedQuantity} {unit}</span>
            </div>
            <div className="border-t border-gray-300 pt-2 mt-2">
              <div className="flex justify-between">
                <span className="text-gray-700 font-medium">If added as new stock:</span>
                <span className="font-bold text-green-600">{newTotal} {unit}</span>
              </div>
            </div>
          </div>

          <p className="text-sm text-gray-600 mb-4">
            How would you like to update the inventory?
          </p>
        </div>

        <div className="space-y-3">
          <button
            onClick={() => {
              onAddStock();
              onClose();
            }}
            className="w-full bg-green-500 text-white px-4 py-3 rounded-lg hover:bg-green-600 transition-colors font-medium text-left"
          >
            <div className="flex items-center justify-between">
              <span>Add as new stock</span>
              <span className="text-sm opacity-90">({currentStock} + {receivedQuantity} = {newTotal} {unit})</span>
            </div>
          </button>

          <button
            onClick={() => {
              onAdjustQuantity();
              onClose();
            }}
            className="w-full bg-blue-500 text-white px-4 py-3 rounded-lg hover:bg-blue-600 transition-colors font-medium text-left"
          >
            <div className="flex items-center justify-between">
              <span>Adjust total quantity</span>
              <span className="text-sm opacity-90">(Set to {receivedQuantity} {unit})</span>
            </div>
          </button>

          <button
            onClick={() => {
              onSkip();
              onClose();
            }}
            className="w-full bg-gray-200 text-gray-700 px-4 py-3 rounded-lg hover:bg-gray-300 transition-colors font-medium"
          >
            Skip this item
          </button>
        </div>
      </div>
    </div>
  );
};

export default InventoryUpdateDialog;
